import { useEffect, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildDashboardScrollKey,
  readDashboardScrollPosition,
  writeDashboardScrollPosition,
} from '@/lib/dashboardScrollState';

const RESTORE_TIMEOUT_MS = 5_000;

export function useDashboardScrollRestoration(scrollRef: RefObject<HTMLElement>) {
  const location = useLocation();
  const scrollKey = buildDashboardScrollKey(location.pathname, location.search);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const desiredTop = readDashboardScrollPosition(sessionStorage, scrollKey);
    let frameId: number | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let lastWriteFrame: number | null = null;
    const restoreStartedAt = Date.now();

    const savePosition = () => {
      writeDashboardScrollPosition(sessionStorage, scrollKey, element.scrollTop);
    };

    const handleScroll = () => {
      if (lastWriteFrame !== null) return;
      lastWriteFrame = window.requestAnimationFrame(() => {
        lastWriteFrame = null;
        savePosition();
      });
    };

    const restorePosition = () => {
      if (desiredTop === null || Date.now() - restoreStartedAt > RESTORE_TIMEOUT_MS) {
        resizeObserver?.disconnect();
        return;
      }

      const maxTop = Math.max(0, element.scrollHeight - element.clientHeight);
      element.scrollTop = Math.min(desiredTop, maxTop);

      if (Math.abs(element.scrollTop - desiredTop) <= 1) {
        resizeObserver?.disconnect();
        return;
      }

      frameId = window.requestAnimationFrame(restorePosition);
    };

    element.addEventListener('scroll', handleScroll, { passive: true });

    if (desiredTop !== null) {
      frameId = window.requestAnimationFrame(restorePosition);
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(restorePosition);
        resizeObserver.observe(element);
      }
    }

    return () => {
      savePosition();
      element.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      if (lastWriteFrame !== null) window.cancelAnimationFrame(lastWriteFrame);
    };
  }, [scrollKey, scrollRef]);
}
